import { useCallback, useEffect, useMemo, useRef } from "react";
import type { ChangeEvent, ClipboardEvent } from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import * as maptalks from "maptalks";
import type { HandlerFn, HandlerFnResultType } from "maptalks/dist/maptalks";
import "maptalks/dist/maptalks.css";
import { useFormikContext } from "formik";
import { useAuth } from "../context/AuthContext";
import { OPENSTREETMAP_TILE_PROXY_PATH, appendMapTileAccessToken } from "../map/tileLayers";
import { PlatformFormValues } from "./PlatformFormGeneral";

const DEFAULT_CENTER: [number, number] = [0.01, 51.5159];
const DEFAULT_ALTITUDE = 18;
const DEFAULT_HEADING = 0;
const DEFAULT_ZOOM = 11;
const STORAGE_KEY = "lastPlatformLocation";

type StoredLocation = {
  center: [number, number];
  zoom: number;
  altitude?: number;
};

type MapClickEvent = HandlerFnResultType & {
  coordinate?: maptalks.Coordinate;
};

const loadStoredLocation = (): StoredLocation | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredLocation;
    if (
      !parsed ||
      !Array.isArray(parsed.center) ||
      parsed.center.length !== 2 ||
      typeof parsed.zoom !== "number"
    ) {
      return null;
    }
    const [lon, lat] = parsed.center.map(Number) as [number, number];
    const altitude =
      parsed.altitude !== undefined && parsed.altitude !== null ? Number(parsed.altitude) : undefined;
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || !Number.isFinite(parsed.zoom)) {
      return null;
    }
    return {
      center: [lon, lat],
      zoom: parsed.zoom,
      altitude: Number.isFinite(altitude) ? altitude : undefined
    };
  } catch (_err) {
    return null;
  }
};

const persistLocation = (location: StoredLocation) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
  } catch (_err) {
    // ignore
  }
};

const PlatformFormGeo = () => {
  const { resolveApiPath, tokens } = useAuth();
  const openStreetMapTileUrl = useMemo(
    () => appendMapTileAccessToken(resolveApiPath(OPENSTREETMAP_TILE_PROXY_PATH), tokens?.accessToken),
    [resolveApiPath, tokens?.accessToken]
  );
  const { values, handleChange, handleBlur, setFieldValue, errors, touched } = useFormikContext<PlatformFormValues>();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maptalks.Map | null>(null);
  const followMapRef = useRef<boolean>(!values.latitude && !values.longitude);
  const markerLayerRef = useRef<maptalks.VectorLayer | null>(null);
  const markerRef = useRef<maptalks.Marker | null>(null);
  const mapClickHandlerRef = useRef<HandlerFn | null>(null);
  const lastElevationCoordsRef = useRef<string | null>(null);
  const latestAltitudeRef = useRef<number | null>(null);

  const updateMarkerPosition = useCallback(
    (lon: number, lat: number, setLocation = false) => {
      const map = mapRef.current;
      const layer = markerLayerRef.current;
      if (!map || !layer || !Number.isFinite(lat) || !Number.isFinite(lon)) {
        return;
      }
      if (!markerRef.current) {
        const marker = new maptalks.Marker([lon, lat], { draggable: true });
        marker.on("dragend", () => {
          const coord = marker.getCoordinates();
          setFieldValue("latitude", String(coord.y), false);
          setFieldValue("longitude", String(coord.x), false);
          setFieldValue("locationSet", true, false);
          followMapRef.current = false;
        });
        marker.addTo(layer);
        markerRef.current = marker;
      } else {
        markerRef.current.setCoordinates([lon, lat]);
      }
      if (setLocation) {
        setFieldValue("locationSet", true, false);
      }
      map.animateTo({ center: [lon, lat] }, { duration: 300 });
    },
    [setFieldValue]
  );

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) {
      return;
    }
    const latitude = Number(values.latitude);
    const longitude = Number(values.longitude);
    const storedLocation = loadStoredLocation();
    const initialCenter: [number, number] =
      Number.isFinite(latitude) && Number.isFinite(longitude)
        ? [longitude, latitude]
        : storedLocation?.center ?? DEFAULT_CENTER;
    const initialZoom = storedLocation?.zoom && Number.isFinite(storedLocation.zoom)
      ? storedLocation.zoom
      : DEFAULT_ZOOM;
    const map = new maptalks.Map(mapContainerRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      centerCross: false,
      baseLayer: new maptalks.TileLayer("base", {
        urlTemplate: openStreetMapTileUrl
      }),
      zoomControl: true
    });
    mapRef.current = map;
    markerLayerRef.current = new maptalks.VectorLayer("platform-location").addTo(map);

    if (values.locationSet && Number.isFinite(latitude) && Number.isFinite(longitude)) {
      const marker = new maptalks.Marker([longitude, latitude], { draggable: true });
      marker.on("dragend", () => {
        const coord = marker.getCoordinates();
        setFieldValue("latitude", String(coord.y), false);
        setFieldValue("longitude", String(coord.x), false);
        setFieldValue("locationSet", true, false);
        followMapRef.current = false;
      });
      marker.addTo(markerLayerRef.current);
      markerRef.current = marker;
    }

    if (followMapRef.current) {
      const center = map.getCenter();
      setFieldValue("latitude", String(center.y), false);
      setFieldValue("longitude", String(center.x), false);
    }

    const handleMove = () => {
      if (!followMapRef.current) {
        return;
      }
      const center = map.getCenter();
      setFieldValue("latitude", String(center.y), false);
      setFieldValue("longitude", String(center.x), false);
    };

    const handleClick: HandlerFn = (event) => {
      const clickEvent = event as MapClickEvent | undefined;
      if (!clickEvent?.coordinate) {
        return;
      }
      const { x, y } = clickEvent.coordinate;
      updateMarkerPosition(x, y, true);
      setFieldValue("latitude", String(y), false);
      setFieldValue("longitude", String(x), false);
      setFieldValue("locationSet", true, false);
      followMapRef.current = false;
    };
    mapClickHandlerRef.current = handleClick;

    map.on("moveend", handleMove);
    map.on("moving", handleMove);
    map.on("click", handleClick);

    requestAnimationFrame(() => map.checkSize());

    return () => {
      if (mapClickHandlerRef.current) {
        map.off("click", mapClickHandlerRef.current);
        mapClickHandlerRef.current = null;
      }
      map.off("moveend", handleMove);
      map.off("moving", handleMove);
      if (mapClickHandlerRef.current) {
        map.off("click", mapClickHandlerRef.current);
        mapClickHandlerRef.current = null;
      }
      const center = map.getCenter();
      const zoom = map.getZoom();
      if (center && Number.isFinite(center.x) && Number.isFinite(center.y) && Number.isFinite(zoom)) {
        persistLocation({
          center: [center.x, center.y],
          zoom,
          altitude: latestAltitudeRef.current ?? undefined
        });
      }
      map.remove();
      mapRef.current = null;
      markerLayerRef.current = null;
      markerRef.current = null;
    };
  }, [openStreetMapTileUrl]);

  useEffect(() => {
    if (values.locationSet) {
      return;
    }
    const map = mapRef.current;
    if (!map) {
      return;
    }
    const storedLocation = loadStoredLocation();
    const [lon, lat] = storedLocation?.center ?? DEFAULT_CENTER;
    const altitude = storedLocation?.altitude ?? DEFAULT_ALTITUDE;
    setFieldValue("latitude", String(lat), false);
    setFieldValue("longitude", String(lon), false);
    setFieldValue("altitude", String(altitude), false);
    setFieldValue("heading", String(DEFAULT_HEADING), false);
    setFieldValue("locationSet", true, false);
    followMapRef.current = false;
    updateMarkerPosition(lon, lat, true);
    if (storedLocation?.zoom && Number.isFinite(storedLocation.zoom)) {
      map.setZoom(storedLocation.zoom);
    }
  }, [setFieldValue, updateMarkerPosition, values.locationSet]);

  const handleFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (followMapRef.current) {
        followMapRef.current = false;
      }
      handleChange(event);
    },
    [handleChange]
  );

  const handlePasteCoords = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      const text = event.clipboardData?.getData("text") ?? "";
      const parts = text.split(/[\s,;]+/).filter(Boolean);
      if (parts.length < 2) {
        return;
      }
      event.preventDefault();
      followMapRef.current = false;
      const [latRaw, lonRaw, altRaw, headingRaw] = parts;
      if (latRaw !== undefined) {
        setFieldValue("latitude", latRaw.trim());
      }
      if (lonRaw !== undefined) {
        setFieldValue("longitude", lonRaw.trim());
      }
      if (altRaw !== undefined) {
        setFieldValue("altitude", altRaw.trim());
      }
      if (headingRaw !== undefined) {
        setFieldValue("heading", headingRaw.trim());
      }
    },
    [setFieldValue]
  );

  useEffect(() => {
    if (!values.locationSet) {
      return;
    }
    const lat = Number(values.latitude);
    const lon = Number(values.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      return;
    }
    if (markerRef.current) {
      const current = markerRef.current.getCoordinates();
      if (current.x === lon && current.y === lat) {
        return;
      }
    }
    updateMarkerPosition(lon, lat);
  }, [updateMarkerPosition, values.latitude, values.locationSet, values.longitude]);

  useEffect(() => {
    const lat = Number(values.latitude);
    const lon = Number(values.longitude);
    const userHasSelectedLocation = values.locationSet || !followMapRef.current;
    if (!userHasSelectedLocation || !Number.isFinite(lat) || !Number.isFinite(lon)) {
      return;
    }

    const coordsKey = `${lat},${lon}`;
    if (lastElevationCoordsRef.current === coordsKey) {
      return;
    }

    const controller = new AbortController();
    const fetchElevation = async () => {
      try {
        const response = await fetch(
          `https://api.open-elevation.com/api/v1/lookup?locations=${lat},${lon}`,
          { signal: controller.signal }
        );
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        const elevation = data?.results?.[0]?.elevation;
        if (!controller.signal.aborted && typeof elevation === "number" && Number.isFinite(elevation)) {
          lastElevationCoordsRef.current = coordsKey;
          setFieldValue("altitude", String(elevation), false);
        }
      } catch (_error) {
        if (!controller.signal.aborted) {
          // ignore errors and let the user continue editing manually
        }
      }
    };

    fetchElevation();

    return () => controller.abort();
  }, [setFieldValue, values.latitude, values.locationSet, values.longitude]);

  useEffect(() => {
    const altitude = Number(values.altitude);
    latestAltitudeRef.current = Number.isFinite(altitude) ? altitude : null;
  }, [values.altitude]);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Platform location
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Click on the map to place a marker, then drag to fine-tune coordinates.
      </Typography>
      <Box
        ref={mapContainerRef}
        sx={{
          mt: 1,
          height: { xs: 260, sm: 320 },
          borderRadius: 1,
          overflow: "hidden",
          "& .maptalks-attribution": { display: "none" }
        }}
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={2}>
        <TextField
          name="latitude"
          label="Latitude"
          type="text"
          value={values.latitude}
          onChange={handleFieldChange}
          onBlur={handleBlur}
          onPaste={handlePasteCoords}
          size="small"
          fullWidth
          sx={{ flex: { sm: 1 } }}
          slotProps={{
            input: { sx: { fontSize: 13, py: 0.75 } },
            inputLabel: { sx: { fontSize: 12 } }
          }}
        />
        <TextField
          name="longitude"
          label="Longitude"
          type="text"
          value={values.longitude}
          onChange={handleFieldChange}
          onBlur={handleBlur}
          onPaste={handlePasteCoords}
          size="small"
          fullWidth
          sx={{ flex: { sm: 1 } }}
          slotProps={{
            input: { sx: { fontSize: 13, py: 0.75 } },
            inputLabel: { sx: { fontSize: 12 } }
          }}
        />
        <TextField
          name="altitude"
          label="Altitude"
          type="text"
          value={values.altitude}
          onChange={handleFieldChange}
          onBlur={handleBlur}
          onPaste={handlePasteCoords}
          size="small"
          fullWidth
          sx={{ flex: { sm: 0.6 } }}
          slotProps={{
            input: { sx: { fontSize: 13, py: 0.75 } },
            inputLabel: { sx: { fontSize: 12 } }
          }}
          error={Boolean(touched.altitude && errors.altitude)}
          helperText={touched.altitude ? errors.altitude : undefined}
        />
        <TextField
          name="heading"
          label="Heading"
          type="text"
          value={values.heading}
          onChange={handleFieldChange}
          onBlur={handleBlur}
          onPaste={handlePasteCoords}
          size="small"
          fullWidth
          sx={{ flex: { sm: 0.5 } }}
          slotProps={{
            input: { sx: { fontSize: 13, py: 0.75 } },
            inputLabel: { sx: { fontSize: 12 } }
          }}
          error={Boolean(touched.heading && errors.heading)}
          helperText={touched.heading ? errors.heading : undefined}
        />
      </Stack>
    </Box>
  );
};

export default PlatformFormGeo;
